import { useCallback } from 'react';
import { useGetConversationHistoryLazyQuery } from '@gonfalon/observability-graphql/hooks';

import { findConversationError } from '../internal/conversation/findConversationError';

import { pickFinalReply } from './pickFinalReply';
import type { AgentProgress } from './readProgress';
import { readProgress } from './readProgress';

// Conversation statuses that mean the run is over (no more polling).
const TERMINAL_STATUSES = new Set(['completed', 'stopped', 'error', 'failed']);

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 120_000;

export class VegaPollTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`no reply after ${Math.round(timeoutMs / 1000)}s`);
    this.name = 'VegaPollTimeoutError';
  }
}

export type VegaPollOptions = {
  onProgress?: (progress: AgentProgress) => void;
  timeoutMs?: number;
  waitForTerminalStatus?: boolean;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Ack returned by any Vega turn mutation (agentPrompt,
// experiment_hypothesis_builder_assist, ...) — it only acks; the reply is async.
export type VegaConversationAck = { conversation_id: string; turn: number };

// Generic poller for any Vega conversation: polls conversation_history (scoped
// by after_turn) until the agent's final reply, then returns its text.
export function useVegaConversation() {
  const [fetchHistory] = useGetConversationHistoryLazyQuery({ fetchPolicy: 'network-only' });

  const poll = useCallback(
    async (
      { conversation_id, turn }: VegaConversationAck,
      signal?: AbortSignal,
      { onProgress, timeoutMs = POLL_TIMEOUT_MS, waitForTerminalStatus = false }: VegaPollOptions = {},
    ): Promise<string> => {
      const deadline = Date.now() + timeoutMs;
      let lastProgress: string | undefined;
      while (Date.now() < deadline) {
        await sleep(POLL_INTERVAL_MS);
        // Caller aborted (e.g. Cancel): stop polling instead of running to the deadline.
        if (signal?.aborted) {
          throw new Error('cancelled');
        }
        const { data, error } = await fetchHistory({
          variables: { conversation_id, after_turn: turn },
          context: { fetchOptions: { signal } },
        });
        if (error) {
          throw error;
        }
        const history = data?.conversation_history;
        const messages = history?.messages ?? [];
        if (onProgress) {
          const progress = readProgress(messages);
          const fingerprint =
            progress && `${progress.kind}:${progress.kind === 'tool' ? progress.name : progress.text}`;
          if (progress && fingerprint !== lastProgress) {
            lastProgress = fingerprint;
            onProgress(progress);
          }
        }
        const reply = pickFinalReply(messages, { anyEnvelope: waitForTerminalStatus });
        if (reply && !waitForTerminalStatus) {
          return reply;
        }
        // Terminal without a usable reply (e.g. vega_error, or the run stopped)
        // — fail fast instead of polling to the timeout.
        const conversationError = findConversationError(messages);
        if (conversationError) {
          throw new Error(conversationError);
        }
        if (history?.status && TERMINAL_STATUSES.has(history.status)) {
          if (reply) {
            return reply;
          }
          // A failed run carries its failure reason on the response — prefer
          // it over the generic message so callers can show why.
          throw new Error(history.error || `Vega run ${history.status} without a reply`);
        }
      }
      throw new VegaPollTimeoutError(timeoutMs);
    },
    [fetchHistory],
  );

  return { poll };
}
